import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Theme from 'utils/theme';
import { Box, Spacer, Text, StatusIndicator } from '@flexisaf/flexibull2';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import PropTypes from 'prop-types';
import request from 'utils/request';
import urls from 'utils/config';
import EmptyCourse from 'assets/images/empty_course.svg';
import { stepTypeColor } from 'components/WorkflowSteps/utils';
import { useSelector } from 'react-redux';
import { selectInstitution } from 'redux/configurations/selectors';
import { FALL_BACK_TZ } from 'utils/constants';
import { calculateDateOffset } from 'utils/helpers';

const moment = require('moment-timezone');

const { API_BASE_URL } = urls || {};

const HistoryWrapper = styled.div`
  width: 100%;
  display: block;
  position: relative;
  margin: 24px;

  & .time-line-header {
    margin-bottom: 20px;
    margin-left: 15px;
  }

  & ul.tl {
    margin: 20px 0;
    padding: 0;
    display: inline-block;
    width: inherit;

    & li {
      list-style: none;
      margin-left: 30px;
      min-height: 50px;
      border-left: 1px dashed ${Theme.PrimaryColor};
      padding: 0 0 30px 30px;
      position: relative;

      &:last-child {
        border-left: 0;
      }
      &::before {
        position: absolute;
        left: -18px;
        top: -5px;
        content: ' ';
        border: 8px solid rgba(255, 255, 255, 0.74);
        border-radius: 500%;
        background: ${Theme.PrimaryColor};
        height: 20px;
        width: 20px;
        transition: all 500ms ease-in-out;
      }

      & .review-box {
        margin-top: -8px;

        & .review-header {
          font-weight: 600;
          font-size: 13px;
          padding: 0 7px;
        }

        & .status-text {
          font-weight: 300 !important ;
          padding: 5px 10px 5px 0;
        }

        & > .time-stamp {
          display: flex;

          margin: 0 2px;

          & .saf-circle {
            font-size: 5px;
            margin-top: 11px;
          }

          & .left {
            margin-right: -5px;
          }

          & .right {
            margin-left: -2px;
          }

          & span {
            font-size: 13px;
            padding: 5px;
          }

          & .separator {
            height: 1px;
            width: 50px;
            background: ${(props) => props.theme.PrimaryGrey};
            margin-top: 14px;
          }
        }

        & .review-content {
          margin-top: 10px;
          max-width: 800px;
          & > span {
            display: block;

            padding: 25px;
            background: ${(props) => props.theme.PrimaryFade};
            border-radius: 3px 3px 0 0;
            font-size: 0.8rem;
          }

          & > .flexi-grid {
            background: ${(props) => props.theme.PrimaryColor};
            color: #fff;
            border-radius: 0 0 3px 3px;
            padding: 10px;

            & span {
              font-size: 0.9rem;
              &:nth-child(1) {
                font-weight: 700;
              }

              &:nth-child(2) {
                font-weight: 300;
                text-align: right;
              }
            }
          }

          & .shortlist {
            background: ${(props) => props.theme.PrimaryYellow};
          }
          & .interview {
            background: ${(props) => props.theme.PrimaryOrange};
          }
          & .approve {
            background: ${(props) => props.theme.PrimaryGreen};
          }
          & .reject {
            background: ${(props) => props.theme.PrimaryRed};
          }
        }
      }
    }
  }
`;

const ActivityLog = ({ applicant, workflowSteps }) => {
  const institution = useSelector(selectInstitution);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (applicant?.id) {
      fetchActivityLog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicant]);

  const fetchActivityLog = async () => {
    setLoading(true);
    const url = `${API_BASE_URL}/candidates/${applicant?.id}/log`;
    const response = await request({ method: 'get', url });
    if (response.success) {
      setLoading(false);
      setActivityLog(response.raw);
    } else {
      setLoading(false);
      return;
    }
  };

  const getStepType = (name) =>
    workflowSteps?.find((step) => step.name === name)?.type;

  if (loading) {
    return (
      <Box width="100%" vAlign>
        <Box width="100%" margin="30px 0" align="center">
          <SkeletonTheme color="#d0d5d933" highlightColor="#c2cad133">
            <Skeleton count={5} height={50} />
          </SkeletonTheme>
        </Box>
      </Box>
    );
  }

  return (
    <>
      {activityLog.length === 0 ? (
        <Box vAlign height="70vh">
          <Box width="200px" margin="0 auto" align="center">
            <img src={EmptyCourse} alt="Empty Course" />
            <Spacer />
            <Text>No Activity Log</Text>
            <Spacer />
          </Box>
        </Box>
      ) : (
        <HistoryWrapper>
          <Text
            block
            bold
            capitalize
            size="16px"
            uppercase
            className="time-line-header"
          >
            Applicant's Process Timeline
          </Text>
          <ul className="tl">
            {activityLog.map((log, i) => (
              <li
                className="tl-item"
                ng-repeat="item in retailer_history"
                key={i}
              >
                <Box className="review-box">
                  <Box display="flex" align="center" vAlign="center">
                    <Text className="review-header">{log?.reviewedBy} </Text>
                    <Text className="status-text">
                      {log?.type === 'STAGE_CHANGE'
                        ? 'changed applicant status to'
                        : 'Updated Candidate Data'}
                    </Text>
                    {log?.type === 'STAGE_CHANGE' && (
                      <StatusIndicator
                        label={log.stageName}
                        color={
                          stepTypeColor[
                            getStepType(log.stageName) || 'REGISTRATION'
                          ]
                        }
                        compound
                      />
                    )}
                  </Box>
                  <Box className="time-stamp">
                    <Text size="14px">
                      {moment(
                        calculateDateOffset(
                          log?.createdAt,
                          institution?.timeZone
                        )
                      )
                        .tz(institution?.timeZone || FALL_BACK_TZ)
                        .format('h:mm:ss a')}
                    </Text>
                    <i className="saf-circle left" />
                    <Box className="separator" />
                    <i className="saf-circle right" />
                    <Text size="14px">
                      {moment(
                        calculateDateOffset(
                          log?.createdAt,
                          institution?.timeZone
                        )
                      )
                        .tz(institution?.timeZone || FALL_BACK_TZ)
                        .format('MMMM Do YYYY')}
                    </Text>
                  </Box>
                  {log?.type === 'STAGE_CHANGE' && (
                    <Box className="review-content">
                      <Text>{log.reason || 'No Comment'}</Text>
                    </Box>
                  )}
                  <Spacer space="8px" />
                </Box>
              </li>
            ))}
          </ul>
        </HistoryWrapper>
      )}
    </>
  );
};

ActivityLog.propTypes = {
  applicant: PropTypes.object,
};

export default ActivityLog;
